/**
 * AccountCard
 *
 * Card por cuenta vinculada. Muestra:
 *  - Status badge (verde/gris/amarillo/rojo) según connection state
 *  - DisplayName + número + local asignado (si tiene)
 *  - Mini-stats (mensajes hoy / 30d)
 *  - Acciones: Reconnect (si desconectada) · Set default · Desvincular
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import {
    MessageCircle,
    MapPin,
    MoreVertical,
    Star,
    RefreshCw,
    Trash2,
    Wifi,
    WifiOff,
    QrCode,
    AlertTriangle,
    Loader2,
    Globe,
    Settings,
    Play,
    Pause,
    Rocket,
    Shield,
    ChevronRight,
} from "lucide-react";
import { Card } from "../../../shared/components/ui/Card";
import { Button } from "../../../shared/components/ui/Button";
import HealthPanel from "./HealthPanel";
import type {
    WhatsAppAccount,
    WhatsAppAccountStatus,
    WhatsAppActivationStatus,
} from "../types";

interface AccountCardProps {
    account: WhatsAppAccount;
    localName?: string;
    onSetDefault: (id: string) => Promise<void> | void;
    onReconnect: (id: string) => Promise<void> | void;
    onUnlink: (id: string) => Promise<void> | void;
    onPause: (id: string) => Promise<void> | void;
    onActivate: (id: string) => Promise<void> | void;
    /** Cuando vuelve a aparecer pending_qr porque se reconectó: abre el modal */
    onShowQr?: (id: string) => void;
}

const ACTIVATION_META: Record<
    WhatsAppActivationStatus,
    { label: string; bg: string; text: string; icon: React.ReactNode }
> = {
    unconfigured: {
        label: "Sin configurar",
        bg: "bg-violet-50",
        text: "text-violet-700",
        icon: <Settings size={11} />,
    },
    warming_up: {
        label: "Warm-up",
        bg: "bg-orange-50",
        text: "text-orange-700",
        icon: <Rocket size={11} />,
    },
    active: {
        label: "Activa",
        bg: "bg-emerald-50",
        text: "text-emerald-700",
        icon: <Play size={11} fill="currentColor" />,
    },
    paused: {
        label: "Pausada",
        bg: "bg-slate-100",
        text: "text-slate-600",
        icon: <Pause size={11} fill="currentColor" />,
    },
};

const STATUS_META: Record<
    WhatsAppAccountStatus,
    { label: string; bg: string; text: string; dot: string; icon: React.ReactNode }
> = {
    connected: {
        label: "Conectada",
        bg: "bg-green-50",
        text: "text-green-700",
        dot: "bg-green-500",
        icon: <Wifi size={11} />,
    },
    disconnected: {
        label: "Desconectada",
        bg: "bg-slate-100",
        text: "text-slate-600",
        dot: "bg-slate-400",
        icon: <WifiOff size={11} />,
    },
    pending_qr: {
        label: "Esperando QR",
        bg: "bg-amber-50",
        text: "text-amber-700",
        dot: "bg-amber-500",
        icon: <QrCode size={11} />,
    },
    reconnecting: {
        label: "Reconectando…",
        bg: "bg-blue-50",
        text: "text-blue-700",
        dot: "bg-blue-500",
        icon: <Loader2 size={11} className="animate-spin" />,
    },
    banned: {
        label: "Bloqueada",
        bg: "bg-red-50",
        text: "text-red-700",
        dot: "bg-red-500",
        icon: <AlertTriangle size={11} />,
    },
};

function timeAgo(iso?: string): string {
    if (!iso) return "—";
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "ahora";
    if (diffMin < 60) return `hace ${diffMin} min`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `hace ${diffHr} h`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `hace ${diffDay} d`;
    return d.toLocaleDateString("es-AR", { day: "numeric", month: "short" });
}

export default function AccountCard({
    account,
    localName,
    onSetDefault,
    onReconnect,
    onUnlink,
    onPause,
    onActivate,
    onShowQr,
}: AccountCardProps) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [working, setWorking] = useState(false);
    const status = STATUS_META[account.status];
    const activation = ACTIVATION_META[account.activationStatus];

    const handle = async (fn: () => Promise<void> | void) => {
        setWorking(true);
        await fn();
        setWorking(false);
        setMenuOpen(false);
    };

    const isConnected = account.status === "connected";
    const isPending = account.status === "pending_qr";
    const isGlobal = account.scope === "global";
    const isUnconfigured = account.activationStatus === "unconfigured";
    const isWarmingUp = account.activationStatus === "warming_up";
    const isPaused = account.activationStatus === "paused";
    const isActive = account.activationStatus === "active";

    return (
        <Card className="p-5 relative">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                        {/* Connection status */}
                        <span
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${status.bg} ${status.text} border border-transparent flex items-center gap-1`}
                        >
                            <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                            {status.label}
                        </span>
                        {/* Activation status — el badge más importante */}
                        <span
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${activation.bg} ${activation.text} border border-transparent flex items-center gap-1`}
                        >
                            {activation.icon}
                            {activation.label}
                        </span>
                        {/* Scope */}
                        {isGlobal ? (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 flex items-center gap-1">
                                <Globe size={9} />
                                Global
                            </span>
                        ) : (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 flex items-center gap-1">
                                <MapPin size={9} />
                                Local
                            </span>
                        )}
                        {account.isDefault && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100 flex items-center gap-1">
                                <Star size={9} fill="currentColor" />
                                Default
                            </span>
                        )}
                    </div>
                    <h3 className="text-base font-bold text-slate-900 truncate">
                        {account.displayName}
                    </h3>
                    <p className="text-xs text-slate-500 font-mono mt-0.5">
                        {account.phoneNumber}
                    </p>
                    {isGlobal ? (
                        <p className="text-[11px] text-blue-600 mt-1 flex items-center gap-1">
                            <Globe size={10} />
                            Usada por todos los locales
                        </p>
                    ) : (
                        localName && (
                            <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                                <MapPin size={10} />
                                {localName}
                            </p>
                        )
                    )}
                </div>

                {/* Menu kebab */}
                <div className="relative">
                    <button
                        onClick={() => setMenuOpen((o) => !o)}
                        aria-label="Acciones de la cuenta"
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                        className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    >
                        <MoreVertical size={16} />
                    </button>
                    {menuOpen && (
                        <>
                            <div
                                className="fixed inset-0 z-30"
                                onClick={() => setMenuOpen(false)}
                            />
                            <div className="absolute right-0 top-8 z-40 bg-white border border-slate-200 rounded-lg shadow-lg min-w-[200px] py-1">
                                {!account.isDefault && isConnected && isActive && (
                                    <MenuItem
                                        icon={<Star size={12} />}
                                        onClick={() =>
                                            handle(() => onSetDefault(account.id))
                                        }
                                        disabled={working}
                                    >
                                        Marcar como default
                                    </MenuItem>
                                )}
                                {/* Setup link cuando hace falta */}
                                {(isUnconfigured || isWarmingUp) && isConnected && (
                                    <MenuItemLink
                                        icon={<Settings size={12} />}
                                        to={`/whatsapp/accounts/${account.id}/setup`}
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        {isUnconfigured ? "Completar setup" : "Ver setup"}
                                    </MenuItemLink>
                                )}
                                {/* Pause / Activate */}
                                {(isActive || isWarmingUp) && (
                                    <MenuItem
                                        icon={<Pause size={12} />}
                                        onClick={() => handle(() => onPause(account.id))}
                                        disabled={working}
                                    >
                                        Pausar envíos
                                    </MenuItem>
                                )}
                                {isPaused && (
                                    <MenuItem
                                        icon={<Play size={12} />}
                                        onClick={() => handle(() => onActivate(account.id))}
                                        disabled={working}
                                    >
                                        Reactivar
                                    </MenuItem>
                                )}
                                {(account.status === "disconnected" ||
                                    account.status === "reconnecting") && (
                                    <MenuItem
                                        icon={<RefreshCw size={12} />}
                                        onClick={() =>
                                            handle(() => onReconnect(account.id))
                                        }
                                        disabled={working}
                                    >
                                        Reconectar
                                    </MenuItem>
                                )}
                                {isPending && onShowQr && (
                                    <MenuItem
                                        icon={<QrCode size={12} />}
                                        onClick={() => {
                                            onShowQr(account.id);
                                            setMenuOpen(false);
                                        }}
                                        disabled={working}
                                    >
                                        Mostrar QR
                                    </MenuItem>
                                )}
                                <div className="my-1 border-t border-slate-100" />
                                <MenuItem
                                    icon={<Trash2 size={12} />}
                                    onClick={() => handle(() => onUnlink(account.id))}
                                    disabled={working}
                                    danger
                                >
                                    Desvincular
                                </MenuItem>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Mini stats (solo si activa) */}
            {isConnected && isActive && (
                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-100">
                    <Stat
                        icon={<MessageCircle size={11} />}
                        label="Hoy"
                        value={account.messagesToday ?? 0}
                    />
                    <Stat
                        icon={<MessageCircle size={11} />}
                        label="Últimos 30d"
                        value={account.messagesLast30d ?? 0}
                    />
                </div>
            )}

            {/* CTA: completar setup */}
            {isUnconfigured && isConnected && (
                <div className="mt-3 pt-3 border-t border-slate-100 bg-violet-50/30 -mx-5 -mb-5 px-5 pb-5 rounded-b-[28px]">
                    <p className="text-[11px] text-violet-800 mb-2 leading-relaxed">
                        ⚙️ Esta cuenta no puede enviar campañas hasta que completes la configuración.
                    </p>
                    <Link to={`/whatsapp/accounts/${account.id}/setup`}>
                        <Button variant="primary" className="w-full">
                            <Settings size={14} />
                            Completar setup
                            <ChevronRight size={14} />
                        </Button>
                    </Link>
                </div>
            )}

            {/* Warm-up progress bar */}
            {isWarmingUp && account.warmupPlan && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center justify-between mb-1.5">
                        <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                            <Rocket size={11} className="text-orange-600" />
                            Warm-up día {account.warmupPlan.currentDay} de{" "}
                            {account.warmupPlan.totalDays}
                        </p>
                        <p className="text-[10px] text-slate-500">
                            <span className="font-bold text-slate-900 tabular-nums">
                                {account.warmupPlan.usedToday}
                            </span>
                            /{account.warmupPlan.dailyLimit} hoy
                        </p>
                    </div>
                    {/* Días progress bar */}
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-orange-400 to-orange-500 transition-all"
                            style={{
                                width: `${
                                    (account.warmupPlan.currentDay /
                                        account.warmupPlan.totalDays) *
                                    100
                                }%`,
                            }}
                        />
                    </div>
                    {account.warmupPlan.nextIncreaseAt.newLimit !== -1 ? (
                        <p className="text-[10px] text-slate-400 mt-1.5">
                            Próximo aumento: día{" "}
                            {account.warmupPlan.nextIncreaseAt.day} →{" "}
                            {account.warmupPlan.nextIncreaseAt.newLimit.toLocaleString(
                                "es-AR",
                            )}{" "}
                            msj/día
                        </p>
                    ) : (
                        <p className="text-[10px] text-slate-400 mt-1.5">
                            Próximo: día {account.warmupPlan.nextIncreaseAt.day} → sin
                            límite
                        </p>
                    )}
                </div>
            )}

            {/* Paused warning */}
            {isPaused && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex items-start gap-2 mb-2">
                        <Shield
                            size={12}
                            className="text-slate-500 mt-0.5 flex-shrink-0"
                        />
                        <p className="text-[11px] text-slate-700 leading-relaxed">
                            Cuenta pausada. No envía campañas hasta que la reactives.
                            {account.health?.warnings?.[0] && (
                                <span className="block text-slate-500 text-[10px] mt-0.5">
                                    Motivo: {account.health.warnings[0]}
                                </span>
                            )}
                        </p>
                    </div>
                    <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => handle(() => onActivate(account.id))}
                        disabled={working}
                    >
                        {working ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <Play size={14} fill="currentColor" />
                        )}
                        Reactivar cuenta
                    </Button>
                </div>
            )}

            {/* Banner para pending_qr (CTA prominente) */}
            {isPending && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                    <Button
                        variant="primary"
                        className="w-full"
                        onClick={() => onShowQr?.(account.id)}
                    >
                        <QrCode size={14} />
                        Escanear QR para activar
                    </Button>
                </div>
            )}

            {/* Banner para disconnected */}
            {account.status === "disconnected" && !isPaused && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                    <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => handle(() => onReconnect(account.id))}
                        disabled={working}
                    >
                        {working ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <RefreshCw size={14} />
                        )}
                        Reconectar
                    </Button>
                </div>
            )}

            {/* Health panel — siempre que haya datos de health */}
            {account.health && <HealthPanel health={account.health} />}

            {/* Last activity footer */}
            {(isConnected || account.status === "disconnected") && (
                <p className="text-[10px] text-slate-400 mt-3 text-right">
                    Última actividad: {timeAgo(account.lastActivityAt)}
                </p>
            )}
        </Card>
    );
}

/* ─── helpers ─── */
function MenuItem({
    icon,
    onClick,
    children,
    disabled,
    danger,
}: {
    icon: React.ReactNode;
    onClick: () => void;
    children: React.ReactNode;
    disabled?: boolean;
    danger?: boolean;
}) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors ${
                danger
                    ? "text-red-600 hover:bg-red-50"
                    : "text-slate-700 hover:bg-slate-50"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
            {icon}
            {children}
        </button>
    );
}

function MenuItemLink({
    icon,
    to,
    onClick,
    children,
}: {
    icon: React.ReactNode;
    to: string;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <Link
            to={to}
            onClick={onClick}
            className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors text-slate-700 hover:bg-slate-50"
        >
            {icon}
            {children}
        </Link>
    );
}

function Stat({
    icon,
    label,
    value,
}: {
    icon: React.ReactNode;
    label: string;
    value: number;
}) {
    return (
        <div className="bg-slate-50 rounded-lg p-2">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1">
                {icon}
                {label}
            </div>
            <div className="text-base font-bold text-slate-900 tabular-nums">
                {value.toLocaleString("es-AR")}
            </div>
        </div>
    );
}
